// Single source of truth for the on-disk track format. The schema is
// the wire format and the future DB format — it travels everywhere.
//
// Forward compatibility is non-negotiable: every field added in a new
// version gets a sane default in loadTrack(), and old saves continue to
// load. Storage is metric/SI throughout — meters, m/s, radians.

export const FORMAT_VERSION = 1;

// 80 km/h. F1 pit lane standard; default for partial / older saves.
const DEFAULT_PIT_SPEED_MS = 22.2;

// A brand-new track with empty defaults. Used as the starting state for
// a fresh editor session and as the base for merging-in older saves.
export function newEmptyTrack() {
  return {
    formatVersion: FORMAT_VERSION,
    name: '',
    author: '',
    description: '',
    trackline: { waypoints: [], width: 16, alpha: 0.5 },
    // Direction of travel. 'cw' (clockwise) or 'ccw' (counter-clockwise).
    // Affects spawn heading + lap-timing crossings; waypoints stay in
    // their authored order regardless.
    direction: 'cw',
    walls: [],
    // Concentric surface bands — collision-fallback safety net used only
    // when no walls are authored (see prototype's
    // applyLegacyRingWallCollision). Modern tracks rely on `walls` and
    // ignore this field.
    surfaces: [
      { type: 'tarmac', edgeDist:  8.0 },
      { type: 'grass',  edgeDist: 22.0 },
      { type: 'gravel', edgeDist: 26.0 },
      { type: 'wall',   edgeDist: 26.6 },
    ],
    surfacePatches: [],
    // Track-context labels (corner numbers + area names). Authoring-only;
    // the simulator does not render them on canvas, but exposes the
    // car's currently-occupied label via car.derived.currentLocation
    // for the HUD/announcer system.
    //   { kind: 'corner', text, segmentIdx, t }
    //   { kind: 'area',   text, startSegIdx, startT, endSegIdx, endT }
    labels: [],
    defaultSurface: 'grass',
    startFinish: { segmentIdx: 0, t: 0.5 },
    startLine:   null,                   // optional aux starting position
    sectors: [],
    spawnPoints: [],                     // multi-car grid; reserved
    pitLane: null,                       // { waypoints, alpha, width, speedLimit, entry, exit, pitBoxes } when authored
    // Authored racing line — closed-loop waypoints the AI follows.
    // null when not authored; the simulator falls back to the
    // centerline. Auto-generate (in the editor) seeds waypoints from
    // the apex-pull algorithm for the user to refine.
    racingLine: null,                    // { waypoints, alpha } when authored
    // Pace car — reserved for full-course-caution implementation.
    // Future shape: { waypoints, speedLimit, pitInExitMarkers } so the
    // safety car has its own path (typically a short loop pulling in
    // from pit, threading the racing line at safety-car pace, then
    // peeling back to pit on the green-flag-drop lap). null = no
    // dedicated pace car authored; FCC currently runs without one
    // (leader paces the field directly via _cautionPaceTarget).
    paceCar: null,
    // Optional aerial / satellite reference image, base64-inline:
    //   { dataUrl, centerX, centerY, metersPerPixel, rotation, opacity }
    // Adds significant file size; null when not in use.
    backgroundImage: null,
    metadata: { createdAt: null, modifiedAt: null },
  };
}

// Validate and normalize a parsed track object. Backfills any fields
// older saves may be missing so the returned shape is always a
// fully-populated current-version track.
//
// Throws on fundamentally malformed input. Returns
//   { track, formatMismatch }
// where `formatMismatch` is the file's formatVersion when it's newer
// than what this code supports (caller decides whether to proceed —
// the editor confirms with the user, the prototype loads anyway). It's
// null when the file matches or predates FORMAT_VERSION.
export function loadTrack(data) {
  if (!data || typeof data !== 'object') {
    throw new Error('Not a valid track file: not an object');
  }
  if (typeof data.formatVersion !== 'number') {
    throw new Error('Not a valid track file: missing formatVersion');
  }
  if (!data.trackline || !Array.isArray(data.trackline.waypoints) || data.trackline.waypoints.length < 3) {
    throw new Error('Not a valid track file: missing or too-short trackline');
  }

  const merged = newEmptyTrack();
  Object.assign(merged, data);

  merged.trackline = { ...newEmptyTrack().trackline, ...data.trackline };
  if (merged.trackline.alpha == null) merged.trackline.alpha = 0.5;
  if (merged.trackline.width == null) merged.trackline.width = 16;

  merged.direction = data.direction === 'ccw' ? 'ccw' : 'cw';

  if (!Array.isArray(merged.walls)) merged.walls = [];
  if (!Array.isArray(merged.surfacePatches)) merged.surfacePatches = [];
  if (!Array.isArray(merged.sectors)) merged.sectors = [];
  if (!Array.isArray(merged.spawnPoints)) merged.spawnPoints = [];
  if (!Array.isArray(merged.labels)) merged.labels = [];
  if (merged.startLine === undefined) merged.startLine = null;
  if (!merged.startFinish) merged.startFinish = { segmentIdx: 0, t: 0.5 };
  if (!merged.defaultSurface) merged.defaultSurface = 'grass';

  if (merged.pitLane) {
    const pl = merged.pitLane;
    if (!Array.isArray(pl.waypoints)) pl.waypoints = [];
    if (pl.alpha == null)       pl.alpha = 0.5;
    if (pl.width == null)       pl.width = 4.0;
    if (pl.speedLimit == null)  pl.speedLimit = DEFAULT_PIT_SPEED_MS;
    if (pl.entry === undefined) pl.entry = null;
    if (pl.exit  === undefined) pl.exit  = null;
    // Speed-zone gates — { segmentIdx, t } in PIT-LANE SPLINE space
    // (not main-track space). speedLimit applies between these two
    // markers. Older saves without these fields default to "entire
    // pit-lane spline" (start of waypoint 0 → end of last segment),
    // which excludes the entry / exit ribbons — closer to F1
    // convention than the previous "limit during entire pit phase".
    if (pl.speedZoneStart === undefined) pl.speedZoneStart = null;
    if (pl.speedZoneEnd   === undefined) pl.speedZoneEnd   = null;
    if (!Array.isArray(pl.pitBoxes)) pl.pitBoxes = [];
    // Pit-box authoring is auto-generated geometry, not editor-clicked
    // shapes. The author picks a side ('left' | 'right' relative to
    // pit-lane spline direction) and the renderer derives a row of
    // fixed-size boxes packed along the speed zone, centered on its
    // midpoint. boxLength / boxDepth override the defaults when a
    // track wants non-standard dimensions (kart vs F1, etc.). Older
    // saves without boxSide get null → no boxes rendered.
    if (pl.boxSide   === undefined) pl.boxSide   = null;
    if (pl.boxLength === undefined) pl.boxLength = null;
    if (pl.boxDepth  === undefined) pl.boxDepth  = null;
  } else {
    merged.pitLane = null;
  }

  // Pace car — reserved schema slot, no authoring UI yet. Old saves
  // without the field, and saves with anything other than a populated
  // object, both round-trip as null.
  if (merged.paceCar === undefined) merged.paceCar = null;

  // Racing line — accepted only when the closed-loop has enough
  // waypoints to densify (≥ 4 for a Catmull-Rom closed loop without
  // degenerate seams). Older saves without this field round-trip
  // unchanged.
  if (merged.racingLine && Array.isArray(merged.racingLine.waypoints)
      && merged.racingLine.waypoints.length >= 4) {
    const rl = merged.racingLine;
    if (rl.alpha == null) rl.alpha = 0.5;
  } else {
    merged.racingLine = null;
  }

  if (merged.backgroundImage && merged.backgroundImage.dataUrl) {
    const bg = merged.backgroundImage;
    if (bg.centerX        == null) bg.centerX = 0;
    if (bg.centerY        == null) bg.centerY = 0;
    if (bg.metersPerPixel == null) bg.metersPerPixel = 0.5;
    if (bg.rotation       == null) bg.rotation = 0;
    if (bg.opacity        == null) bg.opacity = 0.6;
  } else {
    merged.backgroundImage = null;
  }

  if (!merged.metadata) merged.metadata = { createdAt: null, modifiedAt: null };

  return {
    track: merged,
    formatMismatch: data.formatVersion > FORMAT_VERSION ? data.formatVersion : null,
  };
}
